import {Box, createStyles, Group, Stepper, Text, Tooltip} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import notifications, {Response} from 'notifications';
import {ZIndexRole} from 'styles/StyleConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as net from 'util/Net';

import type {Extension} from '../content-store/extensionConstants';
import {
  extensionWithInstallationIdFromInstallResponse,
  EXTENSION_URL_BASE,
} from '../content-store/extensionInstall';
import ExtensionStoreExtensionHeader from '../content-store/ExtensionStoreExtensionHeader';
import {
  buildExtensionInstallRequestBody,
  createEmptyExtensionInstallContext,
  getExtensionInstallWizardSteps,
  type ExtensionInstallContext,
  type ExtensionInstallWizardStep,
} from './registry';

const TOOLTIP_COMPLETE_STEP = 'Complete this step to continue';

const useStyles = createStyles((theme) => ({
  layoutRoot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing.md,
  },
  headerBlock: {
    flexShrink: 0,
  },
  /**
   * Same vertical rhythm as ExtensionDetails after ExtensionStoreExtensionHeader: Stack uses
   * `spacing="lg"` before the next section (no extra padding on the stepper itself).
   */
  stepperSection: {
    flexShrink: 0,
    marginTop: theme.spacing.lg,
  },
  /**
   * Matches ExtensionDetails: `spacing="lg"` from header to the description block, then that block
   * uses `pt="md"` before the copy — same distance from header bottom to dimmed text.
   */
  stepDescriptionSection: {
    flexShrink: 0,
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.md,
  },
  mainScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  footer: {
    flexShrink: 0,
    paddingTop: theme.spacing.lg,
    marginTop: theme.spacing.md,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
  tooltipAnchor: {
    display: 'inline-block',
  },
}));

function ExtensionInstallWizardInner({
  extension,
  steps,
  onCancel,
  onInstalled,
}: {
  extension: Extension;
  steps: Array<ExtensionInstallWizardStep>;
  onCancel: () => void;
  onInstalled: (installedExtension: Extension) => void;
}): JSX.Element {
  const {classes} = useStyles();
  const [installContext, setInstallContext] = useState<ExtensionInstallContext>(() =>
    createEmptyExtensionInstallContext(extension)
  );
  const [active, setActive] = useState(0);
  const [installing, setInstalling] = useState(false);

  const isLast = active === steps.length - 1;

  const canProceed = useMemo(() => {
    const step = steps[active];
    if (step == null) {
      return true;
    }
    return step.canProceed(installContext, extension);
  }, [active, installContext, extension, steps]);

  const goNext = useCallback(() => {
    if (!canProceed) {
      return;
    }
    setActive((i) => Math.min(i + 1, steps.length - 1));
  }, [canProceed, steps.length]);

  const goBack = useCallback(() => {
    setActive((i) => Math.max(i - 1, 0));
  }, []);

  useEffect(() => {
    setActive((current) => Math.min(current, Math.max(0, steps.length - 1)));
  }, [steps.length]);

  const doInstall = useCallback(async () => {
    if (!canProceed) {
      return;
    }
    setInstalling(true);
    try {
      const response = await net.postJSONPromise({
        url: `${EXTENSION_URL_BASE}/${extension.id}:install`,
        data: buildExtensionInstallRequestBody(extension, installContext),
      });
      const installedExtension = extensionWithInstallationIdFromInstallResponse(extension, response);
      notifications.success(`${extension.displayName ?? extension.id} installed successfully.`);
      onInstalled(installedExtension);
    } catch (err: unknown) {
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err as Response);
      } else {
        throw err;
      }
    } finally {
      setInstalling(false);
    }
  }, [canProceed, installContext, extension, onInstalled]);

  const ActiveBody = steps[active]?.StepBody;
  const activeStepDescription = steps[active]?.stepDescription?.trim();

  const primaryDisabled = !canProceed || (isLast && installing);
  const primaryTooltipTitle = isLast && installing ? 'Installing…' : TOOLTIP_COMPLETE_STEP;

  return (
    <Box className={classes.layoutRoot}>
      <Box className={classes.headerBlock}>
        <ExtensionStoreExtensionHeader extension={extension} />
      </Box>

      {steps.length > 1 ? (
        <Box className={classes.stepperSection}>
          <Stepper active={active} onStepClick={setActive} breakpoint="sm" allowNextStepsSelect={false}>
            {steps.map((s) => (
              <Stepper.Step key={s.id} label={s.label} />
            ))}
          </Stepper>
        </Box>
      ) : null}

      {activeStepDescription ? (
        <Box className={classes.stepDescriptionSection}>
          <Text size="sm" c="dimmed">
            {activeStepDescription}
          </Text>
        </Box>
      ) : null}

      <Box className={classes.mainScroll} pt={activeStepDescription ? 'md' : 'lg'}>
        {ActiveBody != null ? (
          <ActiveBody
            extension={extension}
            installContext={installContext}
            setInstallContext={setInstallContext}
          />
        ) : null}
      </Box>

      <Box className={classes.footer}>
        <Group position="right" spacing="sm">
          <Button
            text="Cancel"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MEDIUM}
            onClick={onCancel}
          />
          {active > 0 ? (
            <Button
              text="Back"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MEDIUM}
              onClick={goBack}
            />
          ) : null}
          <Tooltip
            disabled={!primaryDisabled}
            events={{hover: true, focus: true, touch: true}}
            label={primaryTooltipTitle}
            position="top"
            withinPortal={true}
            zIndex={ZIndexRole.TOOLTIP}
          >
            <span className={primaryDisabled ? classes.tooltipAnchor : undefined}>
              <Button
                text={isLast ? 'Install' : 'Next'}
                buttonStyle={ButtonStyle.PRIMARY}
                size={ButtonSize.MEDIUM}
                disabled={primaryDisabled}
                onClick={isLast ? doInstall : goNext}
              />
            </span>
          </Tooltip>
        </Group>
      </Box>
    </Box>
  );
}

interface ExtensionInstallWizardProps {
  extension: Extension;
  onCancel: () => void;
  onInstalled: (installedExtension: Extension) => void;
}

export default function ExtensionInstallWizard({
  extension,
  onCancel,
  onInstalled,
}: ExtensionInstallWizardProps): JSX.Element | null {
  const steps = useMemo(() => getExtensionInstallWizardSteps(extension), [extension]);

  if (steps.length === 0) {
    return null;
  }

  return (
    <ExtensionInstallWizardInner
      key={extension.id}
      extension={extension}
      steps={steps}
      onCancel={onCancel}
      onInstalled={onInstalled}
    />
  );
}
